import { Navigate, useLocation } from 'react-router-dom';
import {
  useAuthStore,
  useIsHydrated,
  type AuthHydrationStatus,
} from '@fog-chess/core';
import { FullScreenLoader } from '@/components/FullScreenLoader';

/**
 * Route guards - Mandate A (prevent hydration flash).
 *
 * Both guards refuse to make a redirect decision while
 * `isHydrated === false` OR `hydrationStatus === 'loading'`. During that
 * window they render the polished FullScreenLoader instead of either the
 * unauthenticated branch (which would falsely bounce to /login) or the
 * authenticated branch (which would flash protected content before the
 * token refresh resolves).
 */

const isHydrating = (
  hydrated: boolean,
  status: AuthHydrationStatus,
): boolean => !hydrated || status === 'loading';

type GuardProps = { children: React.ReactNode };

/**
 * ProtectedRoute - renders children only once auth is confirmed
 * (authenticated + hydrated). Otherwise shows the loader, or redirects
 * to /auth if hydration finished and the user is genuinely unauthenticated.
 */
export function ProtectedRoute({ children }: GuardProps) {
  const isAuthenticated = useAuthStore(
    (s) => !!s.accessToken && !!s.user,
  );
  const isHydrated = useIsHydrated();
  const hydrationStatus = useAuthStore((s) => s.hydrationStatus);
  const location = useLocation();

  if (isHydrating(isHydrated, hydrationStatus)) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/auth" replace state={{ from: location.pathname }} />
    );
  }

  return <>{children}</>;
}

/**
 * PublicRoute - the inverse: only renders public screens (login/register)
 * for genuinely-unauthenticated users. Prevents an authenticated user from
 * landing on /auth again after a token refresh redirects.
 */
export function PublicRoute({ children }: GuardProps) {
  const isAuthenticated = useAuthStore(
    (s) => !!s.accessToken && !!s.user,
  );
  const isHydrated = useIsHydrated();
  const hydrationStatus = useAuthStore((s) => s.hydrationStatus);

  if (isHydrating(isHydrated, hydrationStatus)) {
    return <FullScreenLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
